import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Check, User, Mail, Phone } from "lucide-react";
import { useCustomer } from "../contexts/CustomerContext";
import { toast } from "sonner";

export default function CustomerSettings() {
  const navigate = useNavigate();
  const { customer, token, updateProfile } = useCustomer();
  const [name, setName] = useState(customer?.name || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!customer || !token) {
      navigate("/");
      return;
    }
    setName(customer.name || "");
  }, [customer, token, navigate]);

  const handleSave = async (event) => {
    event.preventDefault();
    if (!name.trim()) {
      toast.error("Please enter your name.");
      return;
    }
    if (name.trim() === customer?.name) {
      toast.error("No changes detected.");
      return;
    }
    setSaving(true);
    const result = await updateProfile({ name: name.trim() });
    setSaving(false);
    if (result.success) {
      toast.success("Profile updated successfully.");
      navigate("/dashboard");
    } else {
      toast.error(result.error || "Unable to update profile.");
    }
  };

  if (!customer) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-6 flex items-center gap-3 text-slate-700">
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-[#0369A1]"
          >
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </button>
        </div>

        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Account settings</h1>
              <p className="text-slate-500 mt-1">Update your name and keep your profile current.</p>
            </div>
            <div className="flex items-center gap-3 text-slate-500 text-sm">
              <User className="w-5 h-5 text-[#0369A1]" />
              {customer.name || "No name set"}
            </div>
          </div>

          <form onSubmit={handleSave} className="space-y-6">
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-slate-700">Name</label>
                <div className="mt-2">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your full name"
                    className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-900 focus:border-[#0369A1] focus:ring-2 focus:ring-[#0369A1]/20 outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">Contact</label>
                <div className="mt-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700">
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-slate-500" />
                    {customer.phone}
                  </div>
                  {customer.email && (
                    <div className="mt-2 flex items-center gap-2 text-sm text-slate-600">
                      <Mail className="w-4 h-4 text-slate-500" />
                      {customer.email}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t border-slate-200 pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-sm text-slate-500 max-w-2xl">
                Only your name can be updated from this screen. Your phone number is used for authentication and cannot be changed here.
              </p>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#0369A1] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#075985] disabled:cursor-not-allowed disabled:opacity-70"
              >
                <Check className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
